<script setup>
import { ref, computed, reactive, watch, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { required, helpers, url } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';

import { useToggle } from '@vueuse/core';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import AgentBotQualifiersAPI from 'dashboard/api/agentBotQualifiers';

const props = defineProps({
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  selectedBot: {
    type: Object,
    default: () => ({}),
  },
});

const MODAL_TYPES = { CREATE: 'create', EDIT: 'edit' };

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const uiFlags = useMapGetter('agentBots/getUIFlags');
const promptTemplates = useMapGetter('promptTemplates/getTemplates');
const selectedTemplateId = ref('');
const activeTab = ref('identity');

const formState = reactive({
  botName: '',
  botDescription: '',
  botUrl: '',
  botAvatar: null,
  botAvatarUrl: '',
  systemPrompt: '',
  faq: '',
  rules: '',
});

// Personality traits
const PERSONALITY_TRAITS = [
  { key: 'formal', label: '🎩 Formal', desc: 'Linguagem profissional e respeitosa' },
  { key: 'friendly', label: '😊 Amigável', desc: 'Tom acolhedor e próximo do cliente' },
  { key: 'direct', label: '🎯 Direto', desc: 'Respostas objetivas, sem rodeios' },
  { key: 'funny', label: '😄 Engraçado', desc: 'Usa humor leve para engajar' },
  { key: 'empathetic', label: '💚 Empático', desc: 'Demonstra compreensão e cuidado' },
  { key: 'technical', label: '🔧 Técnico', desc: 'Termos especializados e precisos' },
  { key: 'persuasive', label: '💎 Persuasivo', desc: 'Orientado a conversão e vendas' },
  { key: 'concise', label: '⚡ Conciso', desc: 'Mensagens curtas e eficientes' },
];

const selectedTraits = ref(new Set());

function toggleTrait(key) {
  if (selectedTraits.value.has(key)) {
    selectedTraits.value.delete(key);
  } else {
    selectedTraits.value.add(key);
  }
  selectedTraits.value = new Set(selectedTraits.value);
  syncRulesFromTraits();
}

// Custom rules are what the user types; personality is generated separately
const customRules = ref('');

function buildFullRules() {
  const traitLines = [...selectedTraits.value].map(key => {
    const trait = PERSONALITY_TRAITS.find(t => t.key === key);
    return trait ? `- ${trait.label}: ${trait.desc}` : '';
  }).filter(Boolean);

  const personalityBlock = traitLines.length
    ? `Personalidade do agente:\n${traitLines.join('\n')}`
    : '';

  const parts = [personalityBlock, customRules.value.trim()].filter(Boolean);
  formState.rules = parts.join('\n\n');
}

function syncRulesFromTraits() {
  buildFullRules();
}

function parseTraitsFromRules(rules) {
  if (!rules) {
    customRules.value = '';
    return;
  }
  const traits = new Set();
  PERSONALITY_TRAITS.forEach(t => {
    if (rules.includes(t.key) || rules.includes(t.label)) {
      traits.add(t.key);
    }
  });
  selectedTraits.value = traits;

  // Extract custom rules (everything not in the personality block)
  let remaining = rules;
  // Remove old marker-style blocks
  remaining = remaining.replace(/<!-- PERSONALITY -->[\s\S]*?<!-- \/PERSONALITY -->/g, '');
  // Remove auto-generated personality block
  remaining = remaining.replace(/Personalidade do agente:[\s\S]*?(?=\n\n|$)/g, '');
  customRules.value = remaining.trim();
}

// FAQ entries
const faqEntries = ref([]);

function addFaqEntry() {
  faqEntries.value.push({ question: '', answer: '' });
}

function removeFaqEntry(index) {
  faqEntries.value.splice(index, 1);
  syncFaqToText();
}

function syncFaqToText() {
  const lines = faqEntries.value
    .filter(e => e.question.trim() || e.answer.trim())
    .map(e => `P: ${e.question.trim()}\nR: ${e.answer.trim()}`)
    .join('\n\n');
  formState.faq = lines;
}

function parseFaqFromText(text) {
  if (!text || !text.trim()) {
    faqEntries.value = [];
    return;
  }
  // Normalize line endings (\r\n -> \n)
  const normalized = text.replace(/\r\n/g, '\n');
  const blocks = normalized.split(/\n\n+/);
  const entries = [];
  blocks.forEach(block => {
    const qMatch = block.match(/^P:\s*(.+)/m);
    const aMatch = block.match(/^R:\s*([\s\S]*)/m);
    if (qMatch || aMatch) {
      entries.push({
        question: qMatch ? qMatch[1].trim() : '',
        answer: aMatch ? aMatch[1].trim() : '',
      });
    }
  });
  faqEntries.value = entries.length ? entries : [];
}

// Document upload
const ACCEPTED_FILE_TYPES = '.pdf,.doc,.docx,.csv,.txt';
const ACCEPTED_LABELS = 'PDF, DOC, DOCX, CSV, TXT';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const uploadedFiles = ref([]);
const isUploading = ref(false);
const fileInputRef = ref(null);

// Knowledge overview (from IA API)
const knowledgeOverview = ref(null);
const isLoadingOverview = ref(false);

// Knowledge test
const testQuestion = ref('');
const testResults = ref(null);
const isTestingKnowledge = ref(false);

async function fetchKnowledgeOverview() {
  if (!props.selectedBot?.id) return;
  isLoadingOverview.value = true;
  try {
    const data = await store.dispatch('agentBots/fetchKnowledgeOverview', props.selectedBot.id);
    knowledgeOverview.value = data;
  } catch {
    knowledgeOverview.value = null;
  } finally {
    isLoadingOverview.value = false;
  }
}

async function deleteKnowledgeDocument(doc) {
  if (!props.selectedBot?.id || !doc?.id) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Remover "${doc.filename}" da base de conhecimento?`)) return;
  try {
    await store.dispatch('agentBots/deleteKnowledgeDocument', {
      botId: props.selectedBot.id,
      documentId: doc.id,
    });
    useAlert(`"${doc.filename}" removido com sucesso.`);
    fetchKnowledgeOverview();
  } catch {
    useAlert(`Erro ao remover "${doc.filename}".`);
  }
}

async function runKnowledgeTest() {
  if (!props.selectedBot?.id || !testQuestion.value.trim()) return;
  isTestingKnowledge.value = true;
  testResults.value = null;
  try {
    const data = await store.dispatch('agentBots/testKnowledge', {
      botId: props.selectedBot.id,
      question: testQuestion.value.trim(),
      topK: 5,
    });
    // Normalize: API returns { answer, sources: { vector_results } }
    const vectorResults = data?.sources?.vector_results || data?.results || [];
    testResults.value = {
      answer: data?.answer || null,
      agent_name: data?.agent_name || null,
      results_count: vectorResults.length,
      results: vectorResults,
    };
  } catch {
    useAlert('Erro ao testar a base de conhecimento.');
  } finally {
    isTestingKnowledge.value = false;
  }
}

// WhatsApp authorized numbers
const whatsappNumbers = ref([]);
const newWhatsappNumber = ref('');

// Split messages option
const splitMessages = ref(false);

function triggerFileInput() {
  fileInputRef.value?.click();
}

async function handleFileUpload(event) {
  const files = Array.from(event.target.files || []);
  if (!files.length) return;

  for (const file of files) {
    if (file.size > MAX_FILE_SIZE) {
      useAlert(`Arquivo "${file.name}" excede o limite de 10MB.`);
      continue;
    }

    const entry = reactive({
      name: file.name,
      size: file.size,
      type: file.type || file.name.split('.').pop(),
      status: 'pending',
      error: null,
    });
    uploadedFiles.value.push(entry);

    // Only upload if bot exists (edit mode)
    if (props.selectedBot?.id) {
      entry.status = 'uploading';
      isUploading.value = true;
      try {
        await store.dispatch('agentBots/uploadKnowledge', {
          botId: props.selectedBot.id,
          file,
        });
        entry.status = 'done';
        useAlert(`"${file.name}" processado com sucesso.`);
      } catch (err) {
        entry.status = 'error';
        entry.error = err?.message || 'Erro ao processar';
        useAlert(`Erro ao processar "${file.name}".`);
      } finally {
        isUploading.value = false;
      }
    } else {
      entry.status = 'queued';
    }
  }

  // Reset file input
  if (fileInputRef.value) fileInputRef.value.value = '';
}

function removeUploadedFile(index) {
  uploadedFiles.value.splice(index, 1);
}

function getFileIcon(name) {
  const ext = name?.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return 'i-lucide-file-text';
  if (['doc', 'docx'].includes(ext)) return 'i-lucide-file-type';
  if (ext === 'csv') return 'i-lucide-file-spreadsheet';
  return 'i-lucide-file';
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function addWhatsappNumber() {
  const num = newWhatsappNumber.value.replace(/\D/g, '');
  if (num.length >= 10 && !whatsappNumbers.value.includes(num)) {
    whatsappNumbers.value.push(num);
    newWhatsappNumber.value = '';
  }
}

function removeWhatsappNumber(index) {
  whatsappNumbers.value.splice(index, 1);
}

function formatPhone(phone) {
  const clean = phone.replace(/\D/g, '');
  if (clean.length === 13) return `+${clean.slice(0, 2)} (${clean.slice(2, 4)}) ${clean.slice(4, 9)}-${clean.slice(9)}`;
  if (clean.length === 11) return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
  return phone;
}

const [showAccessToken, toggleAccessToken] = useToggle();
const accessToken = ref('');

const v$ = useVuelidate(
  {
    botName: {
      required: helpers.withMessage(() => t('AGENT_BOTS.FORM.ERRORS.NAME'), required),
    },
    botUrl: {
      required: helpers.withMessage(() => t('AGENT_BOTS.FORM.ERRORS.URL'), required),
      url: helpers.withMessage(() => t('AGENT_BOTS.FORM.ERRORS.VALID_URL'), url),
    },
  },
  formState
);

const isLoading = computed(() =>
  props.type === MODAL_TYPES.CREATE ? uiFlags.value.isCreating : uiFlags.value.isUpdating
);

const dialogTitle = computed(() => {
  if (showAccessToken.value) return t('AGENT_BOTS.ACCESS_TOKEN.TITLE');
  return props.type === MODAL_TYPES.CREATE ? t('AGENT_BOTS.ADD.TITLE') : t('AGENT_BOTS.EDIT.TITLE');
});

const dialogDescription = computed(() => {
  if (showAccessToken.value) return t('AGENT_BOTS.ACCESS_TOKEN.DESCRIPTION');
  return '';
});

const confirmButtonLabel = computed(() =>
  props.type === MODAL_TYPES.CREATE ? t('AGENT_BOTS.FORM.CREATE') : t('AGENT_BOTS.FORM.UPDATE')
);

const botNameError = computed(() => v$.value.botName.$error ? v$.value.botName.$errors[0]?.$message : '');
const botUrlError = computed(() => v$.value.botUrl.$error ? v$.value.botUrl.$errors[0]?.$message : '');

const showAccessTokenInput = computed(() =>
  showAccessToken.value || props.type === MODAL_TYPES.EDIT || accessToken.value
);

const templateOptions = computed(() => {
  if (!promptTemplates.value?.length) return [];
  return promptTemplates.value.map(t => ({ value: t.id, label: t.name }));
});

const onTemplateSelect = () => {
  const templateId = selectedTemplateId.value;
  if (templateId) {
    const template = promptTemplates.value.find(t => String(t.id) === String(templateId));
    if (template) formState.systemPrompt = template.content;
  }
};

const TABS = [
  { key: 'identity', label: 'Identidade', icon: 'i-lucide-user-circle' },
  { key: 'personality', label: 'Personalidade', icon: 'i-lucide-sparkles' },
  { key: 'knowledge', label: 'Conhecimento', icon: 'i-lucide-book-open' },
  { key: 'qualification', label: 'Qualificação', icon: 'i-lucide-brain' },
  { key: 'prompt', label: 'Avançado', icon: 'i-lucide-terminal' },
];

const hasAdvancedOverride = computed(() => !!formState.systemPrompt?.trim());

// ==================== QUALIFICATION ====================
const pipelines = useMapGetter('crmPipelines/getPipelines');
const qualifiers = ref([]);
const isLoadingQualifiers = ref(false);
const showQualifierForm = ref(false);
const editingQualifier = ref(null);
const qualifierForm = ref({
  name: '', crm_pipeline_id: null, crm_pipeline_stage_id: null,
  webhook_url: '', webhook_headers: {},
  qualified_pipeline_id: null, qualified_stage_id: null,
  not_qualified_pipeline_id: null, not_qualified_stage_id: null,
  active: true,
});
const collectionFields = ref([]);
const qualifierHeaders = ref([]);

const qualifierTriggerStages = computed(() => {
  const pid = qualifierForm.value.crm_pipeline_id;
  if (!pid) return [];
  const p = pipelines.value.find(pp => pp.id === Number(pid));
  return p?.stages || [];
});
const qualifiedStages = computed(() => {
  const pid = qualifierForm.value.qualified_pipeline_id || qualifierForm.value.crm_pipeline_id;
  if (!pid) return [];
  return pipelines.value.find(p => p.id === Number(pid))?.stages || [];
});
const notQualifiedStages = computed(() => {
  const pid = qualifierForm.value.not_qualified_pipeline_id || qualifierForm.value.crm_pipeline_id;
  if (!pid) return [];
  return pipelines.value.find(p => p.id === Number(pid))?.stages || [];
});

async function loadQualifiers() {
  if (!props.selectedBot?.id) return;
  isLoadingQualifiers.value = true;
  try {
    const { data } = await AgentBotQualifiersAPI.getAll(props.selectedBot.id);
    qualifiers.value = data;
  } catch { qualifiers.value = []; }
  finally { isLoadingQualifiers.value = false; }
}

function openCreateQualifier() {
  editingQualifier.value = null;
  qualifierForm.value = {
    name: '', crm_pipeline_id: null, crm_pipeline_stage_id: null,
    webhook_url: '', webhook_headers: {},
    qualified_pipeline_id: null, qualified_stage_id: null,
    not_qualified_pipeline_id: null, not_qualified_stage_id: null,
    active: true,
  };
  collectionFields.value = [];
  qualifierHeaders.value = [];
  showQualifierForm.value = true;
}

function openEditQualifier(q) {
  editingQualifier.value = q;
  qualifierForm.value = {
    name: q.name, crm_pipeline_id: q.crm_pipeline_id,
    crm_pipeline_stage_id: q.crm_pipeline_stage_id,
    webhook_url: q.webhook_url,
    webhook_headers: { ...(q.webhook_headers || {}) },
    qualified_pipeline_id: q.qualified_pipeline_id,
    qualified_stage_id: q.qualified_stage_id,
    not_qualified_pipeline_id: q.not_qualified_pipeline_id,
    not_qualified_stage_id: q.not_qualified_stage_id,

    active: q.active,
  };
  collectionFields.value = (q.config?.collection_fields || []).map(f => ({
    name: f.name, required: f.required || false,
    examples: [...(f.examples || [])], showExamples: (f.examples || []).length > 0,
  }));
  qualifierHeaders.value = Object.entries(q.webhook_headers || {}).map(([k, v]) => ({ key: k, value: v }));
  showQualifierForm.value = true;
}

function addCollectionField() { collectionFields.value.push({ name: '', required: false, examples: [], showExamples: false }); }
function removeCollectionField(idx) { collectionFields.value.splice(idx, 1); }
function addFieldExample(fieldIdx) { collectionFields.value[fieldIdx].examples.push(''); collectionFields.value[fieldIdx].showExamples = true; }
function removeFieldExample(fieldIdx, exIdx) { collectionFields.value[fieldIdx].examples.splice(exIdx, 1); }
function addQualifierHeader() { qualifierHeaders.value.push({ key: '', value: '' }); }
function removeQualifierHeader(idx) { qualifierHeaders.value.splice(idx, 1); }


async function saveQualifier() {
  const f = qualifierForm.value;
  if (!f.name || !f.crm_pipeline_id || !f.crm_pipeline_stage_id) return;
  const headersObj = {};
  qualifierHeaders.value.forEach(h => { if (h.key) headersObj[h.key] = h.value; });
  const payload = {
    ...f, webhook_headers: headersObj,
    config: { collection_fields: collectionFields.value.filter(cf => cf.name.trim()).map(cf => ({
      name: cf.name.trim(), required: cf.required, examples: cf.examples.filter(e => e.trim()),
    })) },
  };
  try {
    if (editingQualifier.value) {
      await AgentBotQualifiersAPI.updateQualifier(props.selectedBot.id, editingQualifier.value.id, payload);
      useAlert('Qualificador atualizado.');
    } else {
      await AgentBotQualifiersAPI.createQualifier(props.selectedBot.id, payload);
      useAlert('Qualificador criado.');
    }
    showQualifierForm.value = false;
    editingQualifier.value = null;
    loadQualifiers();
  } catch (e) {
    useAlert(e.response?.data?.error || 'Erro ao salvar qualificador.');
  }
}

async function deleteQualifier(q) {
  if (!window.confirm(`Excluir qualificador "${q.name}"?`)) return;
  try {
    await AgentBotQualifiersAPI.deleteQualifier(props.selectedBot.id, q.id);
    useAlert('Qualificador excluído.');
    loadQualifiers();
  } catch { useAlert('Erro ao excluir qualificador.'); }
}

async function toggleQualifierActive(q) {
  try {
    await AgentBotQualifiersAPI.updateQualifier(props.selectedBot.id, q.id, { active: !q.active });
    loadQualifiers();
  } catch { useAlert('Erro ao alterar status.'); }
}

const resetForm = () => {
  Object.assign(formState, {
    botName: '', botDescription: '', botUrl: '',
    botAvatar: null, botAvatarUrl: '', systemPrompt: '',
    faq: '', rules: '',
  });
  selectedTemplateId.value = '';
  selectedTraits.value = new Set();
  faqEntries.value = [];
  customRules.value = '';
  whatsappNumbers.value = [];
  newWhatsappNumber.value = '';
  uploadedFiles.value = [];
  knowledgeOverview.value = null;
  testQuestion.value = '';
  testResults.value = null;
  splitMessages.value = false;
  qualifiers.value = [];
  showQualifierForm.value = false;
  editingQualifier.value = null;
  activeTab.value = 'identity';
  v$.value.$reset();
};

const handleImageUpload = ({ file, url: avatarUrl }) => {
  formState.botAvatar = file;
  formState.botAvatarUrl = avatarUrl;
};

const handleAvatarDelete = async () => {
  if (props.selectedBot?.id) {
    try {
      await store.dispatch('agentBots/deleteAgentBotAvatar', props.selectedBot.id);
      formState.botAvatar = null;
      formState.botAvatarUrl = '';
      useAlert(t('AGENT_BOTS.AVATAR.SUCCESS_DELETE'));
    } catch (error) {
      useAlert(t('AGENT_BOTS.AVATAR.ERROR_DELETE'));
    }
  } else {
    formState.botAvatar = null;
    formState.botAvatarUrl = '';
  }
};

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  if (showAccessToken.value) return;

  // Sync FAQ entries to text before save
  syncFaqToText();
  // Rebuild full rules from personality + custom
  buildFullRules();

  const botData = {
    name: formState.botName,
    description: formState.botDescription,
    outgoing_url: formState.botUrl,
    bot_type: 'webhook',
    avatar: formState.botAvatar,
    system_prompt: formState.systemPrompt,
    faq: formState.faq,
    rules: formState.rules,
    bot_config: {
      knowledge_whatsapp_numbers: whatsappNumbers.value.filter(n => n.trim()),
      split_messages: splitMessages.value,
    },
  };

  const isCreate = props.type === MODAL_TYPES.CREATE;

  try {
    const actionPayload = isCreate ? botData : { id: props.selectedBot.id, data: botData };
    const response = await store.dispatch(`agentBots/${isCreate ? 'create' : 'update'}`, actionPayload);
    const alertKey = isCreate ? t('AGENT_BOTS.ADD.API.SUCCESS_MESSAGE') : t('AGENT_BOTS.EDIT.API.SUCCESS_MESSAGE');
    useAlert(alertKey);

    if (isCreate) {
      const { access_token: responseAccessToken, id } = response || {};
      if (id && responseAccessToken) {
        accessToken.value = responseAccessToken;
        toggleAccessToken(true);
      } else {
        accessToken.value = '';
        dialogRef.value.close();
      }
    } else {
      dialogRef.value.close();
    }
    resetForm();
  } catch (error) {
    const errorKey = isCreate ? t('AGENT_BOTS.ADD.API.ERROR_MESSAGE') : t('AGENT_BOTS.EDIT.API.ERROR_MESSAGE');
    useAlert(errorKey);
  }
};

const initializeForm = () => {
  if (props.selectedBot && Object.keys(props.selectedBot).length) {
    const {
      name, description, outgoing_url: botUrl, thumbnail,
      bot_config: botConfig, access_token: botAccessToken,
      system_prompt: botSystemPrompt, faq: botFaq, rules: botRules,
    } = props.selectedBot;
    formState.botName = name || '';
    formState.botDescription = description || '';
    formState.botUrl = botUrl || botConfig?.webhook_url || '';
    formState.botAvatarUrl = thumbnail || '';
    formState.systemPrompt = botSystemPrompt || '';
    formState.faq = botFaq || '';
    formState.rules = botRules || '';
    selectedTemplateId.value = '';

    parseTraitsFromRules(botRules);
    parseFaqFromText(botFaq);

    // Load WhatsApp authorized numbers
    whatsappNumbers.value = botConfig?.knowledge_whatsapp_numbers || [];

    // Load split messages option
    splitMessages.value = botConfig?.split_messages || false;

    if (botAccessToken && props.type === MODAL_TYPES.EDIT) {
      accessToken.value = botAccessToken;
    }
  } else {
    resetForm();
  }
};

const closeModal = () => {
  if (!showAccessToken.value) v$.value?.$reset();
  accessToken.value = '';
  toggleAccessToken(false);
};

const onClickClose = () => {
  closeModal();
  dialogRef.value.close();
};

watch(() => props.selectedBot, initializeForm, { immediate: true, deep: true });

watch(activeTab, (tab) => {
  if (tab === 'knowledge' && props.type === 'edit' && props.selectedBot?.id) {
    fetchKnowledgeOverview();
  }
  if (tab === 'qualification' && props.type === 'edit' && props.selectedBot?.id) {
    loadQualifiers();
    store.dispatch('crmPipelines/get');
  }
});

onMounted(() => {
  store.dispatch('promptTemplates/get');
});

defineExpose({ dialogRef });
</script>

<template>
  <Dialog ref="dialogRef" type="edit" :title="dialogTitle" :description="dialogDescription" :show-cancel-button="false"
    :show-confirm-button="false" width="4xl" @close="closeModal">
    <div class="flex flex-col -mx-6 -mb-6 -mt-2">
      <div v-if="!showAccessToken || type === MODAL_TYPES.EDIT" class="flex flex-col">

        <!-- Tab Navigation -->
        <div class="flex border-b border-n-weak px-6">
          <button
            v-for="tab in TABS" :key="tab.key" type="button"
            class="flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium transition-all cursor-pointer border-b-2 -mb-px"
            :class="activeTab === tab.key
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-n-slate-10 hover:text-n-slate-12 hover:border-n-weak'"
            @click="activeTab = tab.key"
          >
            <span :class="tab.icon" class="size-3.5" />
            {{ tab.label }}
          </button>
        </div>

        <!-- Scrollable tab content -->
        <div class="overflow-y-auto max-h-[60vh] px-6 py-5">

        <!-- TAB: Identity -->
        <div v-show="activeTab === 'identity'" class="flex flex-col gap-4">
          <div class="mb-2 flex flex-col items-start">
            <span class="mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('AGENT_BOTS.FORM.AVATAR.LABEL') }}
            </span>
            <Avatar :src="formState.botAvatarUrl" :name="formState.botName" :size="68" allow-upload
              icon-name="i-lucide-bot-message-square" @upload="handleImageUpload" @delete="handleAvatarDelete" />
          </div>

          <Input id="bot-name" v-model="formState.botName" :label="$t('AGENT_BOTS.FORM.NAME.LABEL')"
            :placeholder="$t('AGENT_BOTS.FORM.NAME.PLACEHOLDER')" :message="botNameError"
            :message-type="botNameError ? 'error' : 'info'" @blur="v$.botName.$touch()" />

          <TextArea id="bot-description" v-model="formState.botDescription"
            :label="$t('AGENT_BOTS.FORM.DESCRIPTION.LABEL')"
            :placeholder="$t('AGENT_BOTS.FORM.DESCRIPTION.PLACEHOLDER')" />

          <Input id="bot-url" v-model="formState.botUrl" :label="$t('AGENT_BOTS.FORM.WEBHOOK_URL.LABEL')"
            :placeholder="$t('AGENT_BOTS.FORM.WEBHOOK_URL.PLACEHOLDER')" :message="botUrlError"
            :message-type="botUrlError ? 'error' : 'info'" @blur="v$.botUrl.$touch()" />

          <!-- Split messages toggle -->
          <div class="flex items-center justify-between rounded-xl border border-n-weak px-4 py-3">
            <div class="flex-1 mr-3">
              <p class="text-sm font-medium text-n-slate-12">Picotar mensagens</p>
              <p class="text-xs text-n-slate-10 mt-0.5">Divide a resposta da IA em várias mensagens menores, simulando uma digitação mais natural.</p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer flex-shrink-0">
              <input v-model="splitMessages" type="checkbox" class="sr-only peer" />
              <div class="w-9 h-5 bg-n-alpha-3 peer-focus:ring-2 peer-focus:ring-blue-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600" />
            </label>
          </div>
        </div>

        <!-- TAB: Personality -->
        <div v-show="activeTab === 'personality'" class="flex flex-col gap-4">
          <div v-if="hasAdvancedOverride" class="rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 px-4 py-2.5 mb-1">
            <div class="flex items-center gap-2">
              <span class="i-lucide-alert-triangle size-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <p class="text-xs text-amber-800 dark:text-amber-300">Estas configurações estão sendo ignoradas porque o <strong>Prompt Avançado</strong> está preenchido.</p>
            </div>
          </div>
          <div :class="{ 'opacity-50 pointer-events-none': hasAdvancedOverride }">
            <p class="text-sm font-semibold text-n-slate-12 mb-1">Como o agente deve se comunicar?</p>
            <p class="text-xs text-n-slate-10 mb-3">Selecione os traços de personalidade do agente. Isso influencia o tom e estilo das respostas.</p>

            <div class="grid grid-cols-2 gap-2">
              <button
                v-for="trait in PERSONALITY_TRAITS" :key="trait.key" type="button"
                class="flex items-start gap-2.5 rounded-xl px-3.5 py-3 border transition-all cursor-pointer text-left"
                :class="selectedTraits.has(trait.key)
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500/30'
                  : 'border-n-weak hover:border-n-slate-6 hover:bg-n-alpha-1'"
                @click="toggleTrait(trait.key)"
              >
                <span class="text-lg leading-none mt-0.5">{{ trait.label.split(' ')[0] }}</span>
                <div class="min-w-0">
                  <span class="text-xs font-semibold block" :class="selectedTraits.has(trait.key) ? 'text-blue-700 dark:text-blue-300' : 'text-n-slate-12'">
                    {{ trait.label.split(' ').slice(1).join(' ') }}
                  </span>
                  <span class="text-[10px] leading-tight block mt-0.5" :class="selectedTraits.has(trait.key) ? 'text-blue-600/70 dark:text-blue-400/70' : 'text-n-slate-9'">
                    {{ trait.desc }}
                  </span>
                </div>
              </button>
            </div>
          </div>

          <div class="mt-2">
            <label class="text-sm font-medium text-n-slate-12 mb-1 block">Regras personalizadas</label>
            <p class="text-xs text-n-slate-10 mb-2">Instruções adicionais sobre como o agente deve se comportar (ex: "Nunca fale sobre concorrentes", "Sempre sugira agendamento").</p>
            <textarea
              v-model="customRules"
              class="w-full px-3 py-2 rounded-lg border border-n-weak bg-transparent text-sm text-n-slate-12 outline-none focus:border-blue-500 resize-y min-h-[80px]"
              placeholder="Ex: Sempre cumprimente pelo nome. Nunca negocie preços abaixo da tabela..."
              rows="4"
            />
          </div>
        </div>

        <!-- TAB: Knowledge (FAQ) -->
        <div v-show="activeTab === 'knowledge'" class="flex flex-col gap-4">
          <div v-if="hasAdvancedOverride" class="rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 px-4 py-2.5 mb-1">
            <div class="flex items-center gap-2">
              <span class="i-lucide-alert-triangle size-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <p class="text-xs text-amber-800 dark:text-amber-300">Estas configurações estão sendo ignoradas porque o <strong>Prompt Avançado</strong> está preenchido.</p>
            </div>
          </div>
          <div :class="{ 'opacity-50 pointer-events-none': hasAdvancedOverride }">
          <div>
            <div class="flex items-center justify-between mb-1">
              <p class="text-sm font-semibold text-n-slate-12">Perguntas e Respostas (FAQ)</p>
              <span class="text-[10px] text-n-slate-9 tabular-nums">{{ faqEntries.length }} {{ faqEntries.length === 1 ? 'entrada' : 'entradas' }}</span>
            </div>
            <p class="text-xs text-n-slate-10 mb-3">Adicione perguntas frequentes para que o agente responda com precisão. Quanto mais respostas, melhor o atendimento.</p>

            <div class="space-y-2 max-h-[350px] overflow-y-auto pr-1">
              <div
                v-for="(entry, idx) in faqEntries" :key="idx"
                class="rounded-xl border border-n-weak p-3 bg-n-alpha-1/30 relative group"
              >
                <button
                  type="button"
                  class="absolute top-2 right-2 w-6 h-6 rounded-lg flex items-center justify-center text-n-slate-9 opacity-0 group-hover:opacity-100 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all cursor-pointer"
                  @click="removeFaqEntry(idx)"
                >
                  <span class="i-lucide-trash-2 size-3" />
                </button>
                <div class="flex flex-col gap-2">
                  <div>
                    <label class="text-[10px] font-bold uppercase tracking-wider text-n-slate-9 mb-0.5 block">Pergunta</label>
                    <input
                      v-model="entry.question"
                      class="w-full px-2.5 py-1.5 rounded-lg border border-n-weak bg-transparent text-xs text-n-slate-12 outline-none focus:border-blue-500"
                      placeholder="Ex: Qual o horário de funcionamento?"
                      @blur="syncFaqToText"
                    />
                  </div>
                  <div>
                    <label class="text-[10px] font-bold uppercase tracking-wider text-n-slate-9 mb-0.5 block">Resposta</label>
                    <textarea
                      v-model="entry.answer"
                      class="w-full px-2.5 py-1.5 rounded-lg border border-n-weak bg-transparent text-xs text-n-slate-12 outline-none focus:border-blue-500 resize-y min-h-[48px]"
                      placeholder="Ex: Funcionamos de segunda a sexta, das 8h às 18h."
                      rows="2"
                      @blur="syncFaqToText"
                    />
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              class="mt-3 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border-2 border-dashed border-n-weak hover:border-blue-400 text-xs font-medium text-n-slate-10 hover:text-blue-600 transition-all cursor-pointer"
              @click="addFaqEntry"
            >
              <span class="i-lucide-plus size-3.5" />
              Adicionar pergunta
            </button>

            <div v-if="!faqEntries.length" class="flex flex-col items-center justify-center py-8 text-center">
              <span class="i-lucide-message-circle-question size-8 text-n-slate-7 mb-2" />
              <p class="text-xs text-n-slate-9">Nenhuma pergunta cadastrada ainda.</p>
              <p class="text-[10px] text-n-slate-8 mt-0.5">Clique em "Adicionar pergunta" para começar.</p>
            </div>
          </div>

          <!-- Document Upload Section -->
          <div class="mt-2 pt-4 border-t border-n-weak">
            <div class="flex items-center justify-between mb-1">
              <p class="text-sm font-semibold text-n-slate-12">Base de conhecimento</p>
              <span class="text-[10px] text-n-slate-9 tabular-nums">{{ uploadedFiles.length }} {{ uploadedFiles.length === 1 ? 'arquivo' : 'arquivos' }}</span>
            </div>
            <p class="text-xs text-n-slate-10 mb-3">Envie documentos para enriquecer o conhecimento do agente. Os arquivos serão processados automaticamente.</p>

            <input
              ref="fileInputRef"
              type="file"
              :accept="ACCEPTED_FILE_TYPES"
              multiple
              class="hidden"
              @change="handleFileUpload"
            />

            <button
              type="button"
              class="w-full flex flex-col items-center justify-center gap-2 py-6 rounded-xl border-2 border-dashed transition-all cursor-pointer"
              :class="isUploading
                ? 'border-blue-300 bg-blue-50/50 dark:bg-blue-900/10'
                : 'border-n-weak hover:border-blue-400 hover:bg-blue-50/30 dark:hover:bg-blue-900/5'"
              :disabled="isUploading"
              @click="triggerFileInput"
            >
              <span v-if="isUploading" class="i-lucide-loader-2 size-6 text-blue-500 animate-spin" />
              <span v-else class="i-lucide-upload-cloud size-6 text-n-slate-8" />
              <div class="text-center">
                <p class="text-xs font-medium" :class="isUploading ? 'text-blue-600' : 'text-n-slate-10'">
                  {{ isUploading ? 'Processando...' : 'Clique para enviar arquivos' }}
                </p>
                <p class="text-[10px] text-n-slate-8 mt-0.5">{{ ACCEPTED_LABELS }} · Máx. 10MB</p>
              </div>
            </button>

            <div v-if="uploadedFiles.length" class="mt-3 space-y-1.5">
              <div
                v-for="(file, idx) in uploadedFiles" :key="idx"
                class="flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-all"
                :class="{
                  'border-n-weak bg-n-alpha-1/30': file.status === 'done' || file.status === 'queued',
                  'border-blue-200 bg-blue-50/30 dark:bg-blue-900/10': file.status === 'uploading',
                  'border-red-200 bg-red-50/30 dark:bg-red-900/10': file.status === 'error',
                }"
              >
                <span :class="getFileIcon(file.name)" class="size-4 flex-shrink-0 text-n-slate-9" />
                <div class="flex-1 min-w-0">
                  <p class="text-xs font-medium text-n-slate-12 truncate">{{ file.name }}</p>
                  <p class="text-[10px] text-n-slate-8">{{ formatFileSize(file.size) }}
                    <span v-if="file.status === 'uploading'" class="text-blue-600"> · Processando...</span>
                    <span v-else-if="file.status === 'done'" class="text-emerald-600"> · Processado</span>
                    <span v-else-if="file.status === 'error'" class="text-red-500"> · {{ file.error }}</span>
                    <span v-else-if="file.status === 'queued'" class="text-amber-600"> · Será enviado após salvar</span>
                  </p>
                </div>
                <button
                  type="button"
                  class="w-6 h-6 rounded-lg flex items-center justify-center text-n-slate-9 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all cursor-pointer flex-shrink-0"
                  @click="removeUploadedFile(idx)"
                >
                  <span class="i-lucide-x size-3" />
                </button>
              </div>
            </div>

            <div v-if="type === 'create' && uploadedFiles.length" class="mt-2 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 px-3 py-2">
              <div class="flex items-center gap-1.5">
                <span class="i-lucide-info size-3 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <p class="text-[10px] text-amber-800 dark:text-amber-300">Os arquivos serão processados após a criação do robô.</p>
              </div>
            </div>
          </div>

          <!-- WhatsApp Knowledge Section -->
          <div class="mt-2 pt-4 border-t border-n-weak">
            <div class="flex items-center gap-2 mb-1">
              <span class="i-lucide-message-circle size-4 text-emerald-600" />
              <p class="text-sm font-semibold text-n-slate-12">Conhecimento via WhatsApp</p>
            </div>
            <p class="text-xs text-n-slate-10 mb-3">Cadastre números autorizados para enviar áudios e documentos de conhecimento via WhatsApp. Somente números pré-autorizados serão aceitos.</p>

            <div class="flex gap-2 mb-3">
              <input
                v-model="newWhatsappNumber"
                class="flex-1 px-3 py-2 rounded-lg border border-n-weak bg-transparent text-sm text-n-slate-12 outline-none focus:border-emerald-500"
                placeholder="Ex: 5511999999999"
                @keydown.enter.prevent="addWhatsappNumber"
              />
              <button
                type="button"
                class="px-4 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer flex items-center gap-1.5"
                :class="newWhatsappNumber.replace(/\D/g, '').length >= 10
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-n-alpha-2 text-n-slate-9 cursor-not-allowed'"
                :disabled="newWhatsappNumber.replace(/\D/g, '').length < 10"
                @click="addWhatsappNumber"
              >
                <span class="i-lucide-plus size-3" />
                Adicionar
              </button>
            </div>

            <div v-if="whatsappNumbers.length" class="space-y-1.5">
              <div
                v-for="(phone, idx) in whatsappNumbers" :key="idx"
                class="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1/30"
              >
                <span class="i-lucide-smartphone size-3.5 text-emerald-600 flex-shrink-0" />
                <div class="flex-1 min-w-0">
                  <p class="text-xs font-medium text-n-slate-12 font-mono">{{ formatPhone(phone) }}</p>
                </div>
                <button
                  type="button"
                  class="w-6 h-6 rounded-lg flex items-center justify-center text-n-slate-9 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all cursor-pointer flex-shrink-0"
                  @click="removeWhatsappNumber(idx)"
                >
                  <span class="i-lucide-x size-3" />
                </button>
              </div>
            </div>

            <div v-else class="flex flex-col items-center justify-center py-6 text-center">
              <span class="i-lucide-smartphone size-6 text-n-slate-7 mb-2" />
              <p class="text-xs text-n-slate-9">Nenhum número autorizado.</p>
              <p class="text-[10px] text-n-slate-8 mt-0.5">Adicione um número para começar a enviar conhecimento via WhatsApp.</p>
            </div>
          </div>

          <!-- Knowledge Overview (from IA API) -->
          <div v-if="type === 'edit'" class="mt-2 pt-4 border-t border-n-weak">
            <div class="flex items-center justify-between mb-1">
              <div class="flex items-center gap-2">
                <span class="i-lucide-database size-4 text-violet-600" />
                <p class="text-sm font-semibold text-n-slate-12">Documentos processados</p>
              </div>
              <button
                type="button"
                class="flex items-center gap-1 text-[10px] font-medium text-blue-600 hover:text-blue-700 transition-colors cursor-pointer"
                :disabled="isLoadingOverview"
                @click="fetchKnowledgeOverview"
              >
                <span class="i-lucide-refresh-cw size-3" :class="{ 'animate-spin': isLoadingOverview }" />
                Atualizar
              </button>
            </div>

            <!-- Loading -->
            <div v-if="isLoadingOverview" class="flex items-center justify-center py-8">
              <span class="i-lucide-loader-2 size-5 text-violet-500 animate-spin" />
            </div>

            <!-- Overview content -->
            <template v-else-if="knowledgeOverview">
              <!-- Summary stats -->
              <div class="grid grid-cols-3 gap-2 mb-3">
                <div class="rounded-lg border border-n-weak bg-n-alpha-1/30 px-3 py-2 text-center">
                  <p class="text-lg font-bold text-n-slate-12 tabular-nums">{{ knowledgeOverview.summary?.total_documents || 0 }}</p>
                  <p class="text-[10px] text-n-slate-9">Documentos</p>
                </div>
                <div class="rounded-lg border border-n-weak bg-n-alpha-1/30 px-3 py-2 text-center">
                  <p class="text-lg font-bold text-n-slate-12 tabular-nums">{{ knowledgeOverview.summary?.total_chunks || 0 }}</p>
                  <p class="text-[10px] text-n-slate-9">Chunks</p>
                </div>
                <div class="rounded-lg border border-n-weak bg-n-alpha-1/30 px-3 py-2 text-center">
                  <p class="text-lg font-bold text-n-slate-12 tabular-nums">{{ knowledgeOverview.summary?.total_size_human || '0 B' }}</p>
                  <p class="text-[10px] text-n-slate-9">Tamanho total</p>
                </div>
              </div>

              <!-- Document list -->
              <div v-if="knowledgeOverview.documents?.length" class="space-y-1.5 max-h-[200px] overflow-y-auto pr-1">
                <div
                  v-for="(doc, idx) in knowledgeOverview.documents" :key="idx"
                  class="flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-all group"
                  :class="{
                    'border-n-weak bg-n-alpha-1/30': doc.status === 'completed',
                    'border-amber-200 bg-amber-50/30 dark:bg-amber-900/10': doc.status === 'processing',
                    'border-red-200 bg-red-50/30 dark:bg-red-900/10': doc.status === 'failed',
                  }"
                >
                  <span :class="getFileIcon(doc.filename)" class="size-4 flex-shrink-0 text-n-slate-9" />
                  <div class="flex-1 min-w-0">
                    <p class="text-xs font-medium text-n-slate-12 truncate">{{ doc.filename }}</p>
                    <p class="text-[10px] text-n-slate-8">
                      {{ doc.chunkCount || 0 }} chunks
                      <span v-if="doc.status === 'completed'" class="text-emerald-600"> · Processado</span>
                      <span v-else-if="doc.status === 'processing'" class="text-amber-600"> · Processando...</span>
                      <span v-else-if="doc.status === 'failed'" class="text-red-500"> · Falhou</span>
                    </p>
                  </div>
                  <span
                    class="size-2 rounded-full flex-shrink-0 group-hover:hidden"
                    :class="{
                      'bg-emerald-500': doc.status === 'completed',
                      'bg-amber-500': doc.status === 'processing',
                      'bg-red-500': doc.status === 'failed',
                    }"
                  />
                  <button
                    type="button"
                    class="hidden group-hover:flex w-6 h-6 rounded-lg items-center justify-center text-n-slate-9 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all cursor-pointer flex-shrink-0"
                    @click="deleteKnowledgeDocument(doc)"
                  >
                    <span class="i-lucide-trash-2 size-3" />
                  </button>
                </div>
              </div>

              <div v-else class="flex flex-col items-center justify-center py-6 text-center">
                <span class="i-lucide-database size-6 text-n-slate-7 mb-2" />
                <p class="text-xs text-n-slate-9">Nenhum documento processado ainda.</p>
                <p class="text-[10px] text-n-slate-8 mt-0.5">Envie arquivos acima para começar.</p>
              </div>
            </template>

            <!-- No data state -->
            <div v-else class="flex flex-col items-center justify-center py-6 text-center">
              <span class="i-lucide-database size-6 text-n-slate-7 mb-2" />
              <p class="text-xs text-n-slate-9">Clique em "Atualizar" para carregar os documentos.</p>
            </div>
          </div>

          <!-- Knowledge Test -->
          <div v-if="type === 'edit'" class="mt-2 pt-4 border-t border-n-weak">
            <div class="flex items-center gap-2 mb-1">
              <span class="i-lucide-flask-conical size-4 text-blue-600" />
              <p class="text-sm font-semibold text-n-slate-12">Testar conhecimento</p>
            </div>
            <p class="text-xs text-n-slate-10 mb-3">Simule uma pergunta do cliente para verificar se a base retorna resultados relevantes.</p>

            <!-- Search-style input -->
            <div class="relative mb-3">
              <span class="i-lucide-message-circle-question size-4 text-n-slate-8 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                v-model="testQuestion"
                class="w-full pl-9 pr-12 py-2.5 rounded-xl border border-n-weak bg-transparent text-sm text-n-slate-12 outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                placeholder="Digite a pergunta que o cliente faria..."
                :disabled="isTestingKnowledge"
                @keydown.enter.prevent="runKnowledgeTest"
              />
              <button
                type="button"
                class="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-lg flex items-center justify-center transition-all cursor-pointer"
                :class="testQuestion.trim() && !isTestingKnowledge
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                  : 'bg-n-alpha-2 text-n-slate-8 cursor-not-allowed'"
                :disabled="!testQuestion.trim() || isTestingKnowledge"
                @click="runKnowledgeTest"
              >
                <span v-if="isTestingKnowledge" class="i-lucide-loader-2 size-3.5 animate-spin" />
                <span v-else class="i-lucide-arrow-right size-3.5" />
              </button>
            </div>

            <!-- Loading -->
            <div v-if="isTestingKnowledge" class="flex flex-col items-center justify-center py-8 gap-2">
              <span class="i-lucide-loader-2 size-6 text-blue-500 animate-spin" />
              <p class="text-xs text-n-slate-9">Buscando na base de conhecimento...</p>
            </div>

            <!-- Results -->
            <template v-else-if="testResults">
              <!-- AI Answer -->
              <div v-if="testResults.answer" class="mb-3 rounded-xl border border-blue-200 dark:border-blue-800/30 bg-blue-50/50 dark:bg-blue-900/10 p-3">
                <div class="flex items-center gap-1.5 mb-1.5">
                  <span class="i-lucide-bot size-3.5 text-blue-600" />
                  <span class="text-[10px] font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wider">
                    {{ testResults.agent_name || 'Agente IA' }}
                  </span>
                </div>
                <p class="text-xs text-n-slate-12 leading-relaxed whitespace-pre-line">{{ testResults.answer }}</p>
              </div>

              <div class="mb-2.5 flex items-center justify-between">
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-list-checks size-3.5 text-n-slate-9" />
                  <span class="text-xs font-medium text-n-slate-10">{{ testResults.results_count || 0 }} fonte(s) encontrada(s)</span>
                </div>
                <button
                  type="button"
                  class="text-[10px] text-blue-600 hover:text-blue-700 font-medium cursor-pointer"
                  @click="testResults = null; testQuestion = ''"
                >
                  Limpar
                </button>
              </div>

              <div v-if="testResults.results?.length" class="space-y-2">
                <div
                  v-for="result in testResults.results" :key="result.rank"
                  class="rounded-xl border border-n-weak p-3 bg-n-alpha-1/30 hover:border-n-slate-6 transition-colors"
                >
                  <div class="flex items-center justify-between mb-1.5">
                    <div class="flex items-center gap-2">
                      <span
                        class="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold"
                        :class="{
                          'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300': result.rank <= 2,
                          'bg-n-alpha-2 text-n-slate-10': result.rank > 2,
                        }"
                      >
                        {{ result.rank }}
                      </span>
                      <span class="text-[10px] font-medium text-n-slate-9 truncate max-w-[200px]">{{ result.source }}</span>
                    </div>
                    <div class="flex items-center gap-1.5">
                      <div class="w-12 h-1.5 rounded-full bg-n-alpha-2 overflow-hidden">
                        <div
                          class="h-full rounded-full transition-all"
                          :class="{
                            'bg-emerald-500': result.score >= 70,
                            'bg-amber-500': result.score >= 40 && result.score < 70,
                            'bg-red-400': result.score < 40,
                          }"
                          :style="{ width: `${Math.min(result.score, 100)}%` }"
                        />
                      </div>
                      <span
                        class="px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums"
                        :class="{
                          'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300': result.score_label === 'alta',
                          'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300': result.score_label === 'média',
                          'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300': result.score_label === 'baixa',
                        }"
                      >
                        {{ result.score?.toFixed(1) }}%
                      </span>
                    </div>
                  </div>
                  <p class="text-xs text-n-slate-11 whitespace-pre-line leading-relaxed line-clamp-4">{{ result.content }}</p>
                </div>
              </div>

              <div v-else class="flex flex-col items-center justify-center py-8 text-center">
                <span class="i-lucide-search-x size-7 text-n-slate-7 mb-2" />
                <p class="text-xs font-medium text-n-slate-9">Nenhum resultado encontrado</p>
                <p class="text-[10px] text-n-slate-8 mt-0.5">Tente reformular a pergunta ou envie mais documentos.</p>
              </div>
            </template>

            <!-- Empty state -->
            <div v-else class="flex flex-col items-center justify-center py-6 text-center">
              <span class="i-lucide-beaker size-7 text-n-slate-6 mb-2" />
              <p class="text-xs text-n-slate-9">Digite uma pergunta acima e pressione <kbd class="px-1.5 py-0.5 rounded bg-n-alpha-2 text-[10px] font-mono font-bold text-n-slate-11">Enter</kbd> para testar</p>
            </div>
          </div>
          </div>
        </div>

        <!-- TAB: Qualificação -->
        <div v-show="activeTab === 'qualification'" class="flex flex-col gap-4">
          <template v-if="type === 'create'">
            <div class="flex flex-col items-center justify-center py-12 text-center">
              <span class="i-lucide-brain size-10 text-n-slate-6 mb-3" />
              <p class="text-sm font-medium text-n-slate-10">Salve o robô primeiro para configurar regras de qualificação.</p>
            </div>
          </template>
          <template v-else>
            <!-- Header -->
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-semibold text-n-slate-12">Regras de Qualificação</p>
                <p class="text-xs text-n-slate-10 mt-0.5">Configure quando e como a IA deve qualificar leads automaticamente.</p>
              </div>
              <NextButton v-if="!showQualifierForm" xs @click="openCreateQualifier">
                <span class="i-lucide-plus size-3" /> Nova Regra
              </NextButton>
            </div>

            <!-- Qualifier Form -->
            <div v-if="showQualifierForm" class="rounded-xl border border-n-weak p-4 space-y-4">
              <div class="flex items-center justify-between">
                <p class="text-sm font-semibold text-n-slate-12">
                  {{ editingQualifier ? 'Editar Regra' : 'Nova Regra' }}
                </p>
                <button type="button" class="w-7 h-7 rounded-lg flex items-center justify-center text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-all cursor-pointer" @click="showQualifierForm = false; editingQualifier = null">
                  <span class="i-lucide-x size-4" />
                </button>
              </div>

              <Input id="qualifier-name" v-model="qualifierForm.name" label="Nome da regra" placeholder="Ex: Qualificar leads quentes" />

              <!-- Trigger section -->
              <div class="rounded-xl border border-n-weak p-4 space-y-3">
                <div class="flex items-center gap-2 mb-1">
                  <span class="i-lucide-zap size-3.5 text-violet-500" />
                  <p class="text-xs font-semibold text-n-slate-12 uppercase tracking-wider">Gatilho</p>
                </div>
                <p class="text-xs text-n-slate-10 -mt-2">Quando a deal entrar nesta etapa, a qualificação será iniciada.</p>
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="text-xs font-medium text-n-slate-12 mb-1.5 block">Pipeline</label>
                    <select v-model="qualifierForm.crm_pipeline_id" class="w-full h-9 rounded-xl border border-n-weak bg-n-alpha-black2 dark:bg-n-solid-2 text-sm text-n-slate-12 outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500/20 transition-all">
                      <option :value="null" disabled>Selecione...</option>
                      <option v-for="p in pipelines" :key="p.id" :value="p.id">{{ p.name }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="text-xs font-medium text-n-slate-12 mb-1.5 block">Etapa</label>
                    <select v-model="qualifierForm.crm_pipeline_stage_id" class="w-full h-9 rounded-xl border border-n-weak bg-n-alpha-black2 dark:bg-n-solid-2 text-sm text-n-slate-12 outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500/20 transition-all" :disabled="!qualifierForm.crm_pipeline_id">
                      <option :value="null" disabled>Selecione...</option>
                      <option v-for="s in qualifierTriggerStages" :key="s.id" :value="s.id">{{ s.name }}</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Outcomes -->
              <div class="grid grid-cols-2 gap-3">
                <div class="rounded-xl border border-n-weak p-4 space-y-3">
                  <div class="flex items-center gap-2">
                    <span class="i-lucide-check-circle size-3.5 text-emerald-500" />
                    <p class="text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">Qualificado</p>
                  </div>
                  <div>
                    <label class="text-xs font-medium text-n-slate-12 mb-1.5 block">Pipeline</label>
                    <select v-model="qualifierForm.qualified_pipeline_id" class="w-full h-9 rounded-xl border border-n-weak bg-n-alpha-black2 dark:bg-n-solid-2 text-sm text-n-slate-12 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/20 transition-all">
                      <option :value="null">Manter atual</option>
                      <option v-for="p in pipelines" :key="p.id" :value="p.id">{{ p.name }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="text-xs font-medium text-n-slate-12 mb-1.5 block">Mover para etapa</label>
                    <select v-model="qualifierForm.qualified_stage_id" class="w-full h-9 rounded-xl border border-n-weak bg-n-alpha-black2 dark:bg-n-solid-2 text-sm text-n-slate-12 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/20 transition-all">
                      <option :value="null">Não mover</option>
                      <option v-for="s in qualifiedStages" :key="s.id" :value="s.id">{{ s.name }}</option>
                    </select>
                  </div>
                </div>
                <div class="rounded-xl border border-n-weak p-4 space-y-3">
                  <div class="flex items-center gap-2">
                    <span class="i-lucide-x-circle size-3.5 text-red-500" />
                    <p class="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wider">Não Qualificado</p>
                  </div>
                  <div>
                    <label class="text-xs font-medium text-n-slate-12 mb-1.5 block">Pipeline</label>
                    <select v-model="qualifierForm.not_qualified_pipeline_id" class="w-full h-9 rounded-xl border border-n-weak bg-n-alpha-black2 dark:bg-n-solid-2 text-sm text-n-slate-12 outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/20 transition-all">
                      <option :value="null">Manter atual</option>
                      <option v-for="p in pipelines" :key="p.id" :value="p.id">{{ p.name }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="text-xs font-medium text-n-slate-12 mb-1.5 block">Mover para etapa</label>
                    <select v-model="qualifierForm.not_qualified_stage_id" class="w-full h-9 rounded-xl border border-n-weak bg-n-alpha-black2 dark:bg-n-solid-2 text-sm text-n-slate-12 outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/20 transition-all">
                      <option :value="null">Não mover</option>
                      <option v-for="s in notQualifiedStages" :key="s.id" :value="s.id">{{ s.name }}</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Collection Fields -->
              <div class="rounded-xl border border-n-weak p-4 space-y-3">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="i-lucide-clipboard-list size-3.5 text-blue-500" />
                    <p class="text-xs font-semibold text-n-slate-12 uppercase tracking-wider">Dados a coletar</p>
                  </div>
                  <button type="button" class="text-xs font-medium text-woot-500 hover:text-woot-600 cursor-pointer flex items-center gap-1" @click="addCollectionField">
                    <span class="i-lucide-plus size-3" /> Adicionar
                  </button>
                </div>
                <p class="text-xs text-n-slate-10 -mt-1">Dados que a IA deve coletar do lead durante a conversa.</p>
                <div class="space-y-2">
                  <div v-for="(field, idx) in collectionFields" :key="idx" class="rounded-xl border border-n-weak p-3 bg-n-alpha-1/30 relative group">
                    <button type="button" class="absolute top-2 right-2 w-6 h-6 rounded-lg flex items-center justify-center text-n-slate-9 opacity-0 group-hover:opacity-100 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all cursor-pointer" @click="removeCollectionField(idx)">
                      <span class="i-lucide-trash-2 size-3" />
                    </button>
                    <div class="space-y-2">
                      <input v-model="field.name" class="w-full h-9 px-3 rounded-xl border border-n-weak bg-n-alpha-black2 dark:bg-n-solid-2 text-sm text-n-slate-12 outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500/20 transition-all" placeholder="Nome do campo (ex: Orçamento)" />
                      <div class="flex items-center gap-4">
                        <label class="flex items-center gap-1.5 text-xs text-n-slate-10 cursor-pointer">
                          <input v-model="field.required" type="checkbox" class="rounded border-n-weak" /> Obrigatório
                        </label>
                        <button type="button" class="text-xs text-woot-500 hover:text-woot-600 cursor-pointer" @click="addFieldExample(idx)">+ Exemplo</button>
                      </div>
                      <div v-if="field.showExamples && field.examples.length" class="space-y-1.5 pl-3 border-l-2 border-n-weak">
                        <div v-for="(ex, exIdx) in field.examples" :key="exIdx" class="flex items-center gap-1.5">
                          <input v-model="field.examples[exIdx]" class="flex-1 h-8 px-3 rounded-lg border border-n-weak bg-n-alpha-black2 dark:bg-n-solid-2 text-xs text-n-slate-11 outline-none focus:border-woot-500 transition-all" placeholder="Exemplo de resposta" />
                          <button type="button" class="w-6 h-6 rounded-lg flex items-center justify-center text-n-slate-9 hover:text-red-500 cursor-pointer" @click="removeFieldExample(idx, exIdx)"><span class="i-lucide-x size-3" /></button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Actions -->
              <div class="flex items-center justify-end gap-2 pt-3 border-t border-n-weak">
                <NextButton faded slate xs @click="showQualifierForm = false; editingQualifier = null">
                  Cancelar
                </NextButton>
                <NextButton xs @click="saveQualifier">
                  {{ editingQualifier ? 'Atualizar' : 'Criar' }}
                </NextButton>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="isLoadingQualifiers" class="flex justify-center py-10">
              <span class="i-lucide-loader-2 size-5 text-n-slate-7 animate-spin" />
            </div>

            <!-- Qualifier list -->
            <div v-else-if="qualifiers.length" class="space-y-2">
              <div v-for="q in qualifiers" :key="q.id" class="rounded-xl border p-3.5 transition-all group" :class="q.active ? 'border-n-weak bg-n-alpha-1/30 hover:border-n-slate-5' : 'border-n-weak/50 bg-n-alpha-1/10 opacity-50'">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3 min-w-0">
                    <div class="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" :class="q.active ? 'bg-violet-100 dark:bg-violet-900/30' : 'bg-n-alpha-2'">
                      <span class="i-lucide-brain size-4" :class="q.active ? 'text-violet-600 dark:text-violet-400' : 'text-n-slate-9'" />
                    </div>
                    <div class="min-w-0">
                      <div class="flex items-center gap-2">
                        <span class="text-sm font-medium text-n-slate-12 truncate">{{ q.name }}</span>
                        <span class="text-[10px] px-1.5 py-0.5 rounded-full font-medium" :class="q.active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-n-alpha-2 text-n-slate-9'">
                          {{ q.active ? 'Ativo' : 'Inativo' }}
                        </span>
                      </div>
                      <p class="text-xs text-n-slate-10 mt-0.5 truncate">
                        {{ q.pipeline_name }} → {{ q.stage_name }}
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0">
                    <button type="button" class="w-7 h-7 rounded-lg flex items-center justify-center text-n-slate-9 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all cursor-pointer" @click="openEditQualifier(q)">
                      <span class="i-lucide-pencil size-3.5" />
                    </button>
                    <button type="button" class="w-7 h-7 rounded-lg flex items-center justify-center text-n-slate-9 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all cursor-pointer" @click="toggleQualifierActive(q)">
                      <span :class="q.active ? 'i-lucide-pause' : 'i-lucide-play'" class="size-3.5" />
                    </button>
                    <button type="button" class="w-7 h-7 rounded-lg flex items-center justify-center text-n-slate-9 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all cursor-pointer" @click="deleteQualifier(q)">
                      <span class="i-lucide-trash-2 size-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty state -->
            <div v-else-if="!showQualifierForm" class="flex flex-col items-center justify-center py-12 text-center">
              <div class="w-12 h-12 rounded-2xl bg-violet-100 dark:bg-violet-900/20 flex items-center justify-center mb-3">
                <span class="i-lucide-brain size-6 text-violet-500" />
              </div>
              <p class="text-sm font-medium text-n-slate-10">Nenhuma regra de qualificação configurada.</p>
              <p class="text-xs text-n-slate-8 mt-1">Crie uma regra para qualificar leads automaticamente via IA.</p>
            </div>
          </template>
        </div>


        <!-- TAB: System Prompt (Advanced) -->
        <div v-show="activeTab === 'prompt'" class="flex flex-col gap-4">
          <div class="rounded-xl border px-4 py-3" :class="hasAdvancedOverride ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800/30' : 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800/30'">
            <div class="flex items-start gap-2">
              <span class="size-4 mt-0.5 flex-shrink-0" :class="hasAdvancedOverride ? 'i-lucide-check-circle text-blue-600 dark:text-blue-400' : 'i-lucide-info text-amber-600 dark:text-amber-400'" />
              <p class="text-xs" :class="hasAdvancedOverride ? 'text-blue-800 dark:text-blue-300' : 'text-amber-800 dark:text-amber-300'">
                <template v-if="hasAdvancedOverride">
                  <strong>Modo avançado ativo.</strong> O prompt abaixo será usado diretamente, ignorando as configurações de Personalidade e Conhecimento.
                </template>
                <template v-else>
                  Se preenchido, este prompt substituirá as configurações de Personalidade e Conhecimento. Deixe vazio para usar o modo básico.
                </template>
              </p>
            </div>
          </div>

          <div v-if="templateOptions.length" class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('AGENT_BOTS.FORM.SYSTEM_PROMPT.TEMPLATE_LABEL') }}
            </label>
            <Select id="prompt-template-select" v-model="selectedTemplateId" :options="templateOptions"
              :placeholder="$t('AGENT_BOTS.FORM.SYSTEM_PROMPT.TEMPLATE_PLACEHOLDER')" class="!w-full"
              @update:model-value="onTemplateSelect" />
          </div>

          <TextArea id="system-prompt" v-model="formState.systemPrompt"
            :label="$t('AGENT_BOTS.FORM.SYSTEM_PROMPT.LABEL')"
            :placeholder="$t('AGENT_BOTS.FORM.SYSTEM_PROMPT.PLACEHOLDER')" :rows="10" />
        </div>
        </div>
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-6 py-4 border-t border-n-weak bg-n-alpha-3">
        <NextButton faded slate type="reset" :label="$t('AGENT_BOTS.FORM.CANCEL')" @click="onClickClose()" />
        <NextButton v-if="!showAccessToken" data-testid="label-submit" :label="confirmButtonLabel"
          :is-loading="isLoading" :disabled="v$.$invalid" @click="handleSubmit" />
      </div>
    </div>
  </Dialog>
</template>
